import React from 'react';

import { Autocomplete, CircularProgress, TextField, Box, Typography, Link, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';

import InputAdornment from '@mui/material/InputAdornment';
import { IconWrapper } from 'components/Icon';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes';
import { getCompanies } from 'features/frameworkAgreements/api/getCompanies';
import { ImageAvatar } from 'components/Avatar';

const BuyerItemCard = ({
  Name,
  VAT,
  company_id,
  square_logo_url,
  CompanyProfile,
  onResultClick,
  ...rest
}) => {
  const navigate = useNavigate();
  return (
    <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
      <Link
        onClick={() => {
          navigate(paths.public.companyPublicProfile("buyer", CompanyProfile?.slug));
          onResultClick();
        }}
        underline="none"
        color="inherit"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
        >
          <ImageAvatar
            img={square_logo_url || ''}
            name={Name}
            searchResult
            sx={{ flexShrink: 0 }}
          />
          <Typography variant="body1" fontWeight="bold">
            {Name} ({VAT})
          </Typography>
        </Stack>
      </Link>
    </Stack>
  );
};

const NoPopper = ({ children, open }) => (open ? children : null);

export const BuyerSearch = ({ onResultClick = () => null }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const { data, refetch } = useQuery(
    ['buyers', inputValue],
    () => getCompanies('buyer', inputValue, false, true, true),
    {
      enabled: !!inputValue,
      initialData: { data: [] },
      select: ({ data }) => {
        return data.map((item) => ({
          ...item,
          label: typeof item.Name === 'number' ? item.Name.toString() : item.Name,
        }));
      },
      onSettled: () => {
        setLoading(() => false);
      },
    }
  );

  const debouncedSetInputValue = useDebouncedCallback(
    (newInputValue) => setInputValue(() => newInputValue),
    300
  );
  useEffect(() => {
    if (inputValue) refetch();
    // eslint-disable-next-line
  }, [inputValue]);

  return (
    <Autocomplete
      fullWidth
      forcePopupIcon={false}
      disablePortal
      value={null}
      blurOnSelect={true}
      options={data}
      getOptionLabel={(item) => item.label}
      filterOptions={(x) => x}
      onChange={(event, newValue) => {
        event.preventDefault();
      }}
      open= {!loading && inputValue.length >= 1}
      onInputChange={(_, newInputValue) => {
        setLoading(() => !!newInputValue);
        debouncedSetInputValue(newInputValue);
      }}
      noOptionsText={inputValue ? t('website:buyerSearch.noResults') : ''}
      renderOption={(props, option) => <BuyerItemCard {...{ ...props, ...option, onResultClick }} />}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('website:buyerSearch.placeholder')}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <IconWrapper icon="search" sx={{ svg: { height: 20 } }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress color="primary" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            root: {
              sx: {
                '.MuiInputBase-root': {
                  backgroundColor: 'white',
                  p: 0,
                  borderRadius: 1,
                  width: {xs: 1, md: 1},
                  // height: 52,
                  minWidth: 200,
                  pl: 2,
                  pr: 1,
                },
                '.MuiInputBase-input': { px: '0px!important' },
              },
            },
          }}
        />
      )}
      slotProps={{
        listbox: {
          sx: {
            p: 0,
            '& > *': {
              p: '16px !important',
              bgcolor: '#F7F7F7 !important',
              borderRadius: "10px",
              boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
              my: 1,
            },
            '& > *:hover': { bgcolor: '#F7F7F7 !important' },
          },
        },
        paper: {
          sx: {
            boxShadow: 'none',
            '& > .MuiAutocomplete-noOptions': {
              my: 1,
              borderRadius: "10px",
              boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
              bgcolor: '#F7F7F7 !important',
              p: "10px !important",
              height: 40,
              display: 'flex',
              alignItems: 'center',
              pl: 2,
            }
          },
        },
      }}
      slots={{
        popper: NoPopper,
      }}
    />
  );
};
